import React, {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { v4 } from 'uuid';
import { QueryClient, QueryClientProvider } from 'react-query';
import {
  Brief,
  BriefSection,
  DynamicSection,
  isMandatoryAndEmpty,
  BriefSectionCtaType,
} from '@fiverr-private/ai_brief';
import { useStepper } from '@fiverr-private/navigation';
import { Gql } from '@fiverr-private/dynamic_matching';
import { NOT_SURE_METADATA_KEY, Step } from '../constants';
import { BriefClassificationResponse, IntentClassification } from '../api';
import { appTranslate } from '../i18n';
import { Currency } from '../types/currency';
import { briefEditSectionClickAction, DEFAULT_BI_PARENT_ELEMENT } from '../monitoring';
import { useIsUserFromRestrictedCountry } from '../hooks';
import { normalizeLineEndings } from '../utils';
import { createUseContext, isHighSpendExperience } from './utils';
import { defaultCurrency } from './BriefCreationContext.constants';

export interface RolloutsList {
  inDolphinsRewardsLaunch?: boolean;
  inDolphinsJobPostMigration?: boolean;
  inMustangsPersonBrief?: boolean;
  inMustangsMixpanelRecording?: boolean;
  inMustangsAuctionInviteFlowExperiment?: boolean;
  inMiraCrowsPythonsAgenticBriefs?: boolean;
}

export interface Rollouts {
  rollouts?: RolloutsList;
}

export type BriefCreationVariant = 'drawer' | 'onboarding_routing';

export interface BriefInitialValues {
  needType: Gql.DmBriefNeedType;
  pricingType: Gql.BriefPricingType;
}

export interface BriefCreationContextProviderProps extends Rollouts {
  brief?: Brief;
  isOpen?: boolean;
  variant?: BriefCreationVariant;
  setIsOpen?: (isOpen: boolean) => void;
  initialStep?: Step;
  contextId?: string;
  briefClassification?: BriefClassificationResponse;
  currency: Currency;
  source?: string | null;
  parentElement?: string;
  onSuccess?: () => void;
  initialPrompt?: string;
  onOpen?: () => void;
  briefInitialValues?: Partial<BriefInitialValues>;
  enableEnhancedUx?: boolean;
  enableScreeningQuestions?: boolean;
  inJpHighSpendExperience?: boolean;
}

export interface BriefCreationContextProps extends BriefCreationContextProviderProps {
  setStep: Dispatch<SetStateAction<Step>>;
  currentStep: Step;
  setBrief: Dispatch<SetStateAction<Brief | null>>;
  contextId: string;
  userInput: string;
  setUserInput: (userInput: string) => void;
  isBriefLoading: boolean;
  setIsBriefLoading: (isBriefLoading: boolean) => void;
  setBriefClassification: (classification: BriefClassificationResponse | undefined) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  initialStep: Step;
  placeholder: string;
  intentClassification: IntentClassification | undefined;
  setIntentClassification: (intentClassification: IntentClassification) => void;
  missingFieldsCount: number;
  focusFirstMissingSection: () => void;
  buyerType: Gql.BuyerType | undefined;
  setBuyerType: Dispatch<SetStateAction<Gql.BuyerType | undefined>>;
  regenerateContextId: () => string;
  createAbortController: () => AbortController;
  onSuccess?: () => void;
  variant: BriefCreationVariant;
  parentElement: string;
  isAllocatedAuctionInviteFlow: boolean;
  setIsAllocatedAuctionInviteFlow: (isAllocatedAuctionInviteFlow: boolean) => void;
  enableEnhancedUx: boolean;
  enableScreeningQuestions: boolean;
  isDiscardModalOpen: boolean;
  openDiscardModal: () => void;
  closeDiscardModal: () => void;
  inJpHighSpendExperience: boolean;
}

export const BriefCreationContext = createContext<BriefCreationContextProps | null>(null);

BriefCreationContext.displayName = 'BriefCreationContext';
const createSectionHandler =
  (
    briefContextId: string,
    elementType: 'add_section' | 'update_section' | 'remove_section',
    variant: BriefCreationVariant,
    source: string | null | undefined
  ) =>
  (section: BriefSection | DynamicSection, ctaType?: BriefSectionCtaType) => {
    briefEditSectionClickAction({
      variant,
      eventSource: source || '',
      briefContextId,
      isMandatory: !!section?.mandatory,
      elementType,
      elementName: (section as any)?.metadata?.question?.metadata_type || section?.type,
      elementTitle: section?.title || '',
      isNotSure: (section?.content && section?.content[0] === NOT_SURE_METADATA_KEY) || false,
      sectionType:
        (section as any)?.metadata?.question?.selection_type ||
        (section as DynamicSection)?.mandatoryType ||
        section?.type ||
        'text',
      ctaType,
    });
  };

export const BriefCreationContextProvider: FC<BriefCreationContextProviderProps> = ({
  children,
  brief: initialBrief,
  variant = 'drawer',
  isOpen: initialIsOpen = false,
  briefClassification: initialBriefClassification,
  initialStep = Step.Creation,
  contextId: initialContextId,
  currency = defaultCurrency,
  parentElement = DEFAULT_BI_PARENT_ELEMENT,
  initialPrompt = '',
  setIsOpen,
  source,
  rollouts,
  onSuccess,
  onOpen,
  briefInitialValues,
  enableEnhancedUx = false,
  enableScreeningQuestions = false,
  inJpHighSpendExperience = false,
}) => {
  const shouldShowComingSoonStep = useIsUserFromRestrictedCountry();
  const actualInitialStep = shouldShowComingSoonStep ? Step.ComingSoon : initialStep;

  const [queryClient] = useState(() => new QueryClient());
  const [currentStep, setStep] = useState(actualInitialStep);
  const [buyerType, setBuyerType] = useState<Gql.BuyerType | undefined>(undefined);

  const [contextId, setContextId] = useState(initialContextId || '');

  const hasBeenOpened = useRef(false);

  const sectionHandlers = useMemo(
    () => ({
      onAdd: createSectionHandler(contextId, 'add_section', variant, source),
      onEditEnd: createSectionHandler(contextId, 'update_section', variant, source),
      onHide: createSectionHandler(contextId, 'remove_section', variant, source),
    }),
    [contextId, variant, source]
  );

  const enrichBriefSectionsWithEvents = useCallback(
    (brief: Brief): BriefSection[] => {
      const sections = brief?.sections?.map((section) => ({
        ...section,
        ...sectionHandlers,
      }));

      return sections || [];
    },
    [sectionHandlers]
  );

  const { setStep: setStepInternal } = useStepper({
    steps: Object.keys(Step).map((step) => ({
      label: step,
    })),
    initialStep: actualInitialStep,
  });

  const [isOpenInternal, setIsOpenInternal] = useState(initialIsOpen);
  const [userInput, setUserInput] = useState(initialPrompt || '');
  const [isBriefLoading, setIsBriefLoading] = useState(false);
  const [briefClassification, setBriefClassification] = useState<BriefClassificationResponse | undefined>(
    initialBriefClassification
  );
  const [placeholder] = useState<string>(appTranslate('input_placeholder') as string);
  const [intentClassification, setIntentClassification] = useState<IntentClassification | undefined>();
  const abortControllerRef = useRef<AbortController | null>(null);
  const [isAllocatedAuctionInviteFlow, setIsAllocatedAuctionInviteFlow] = useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const openDiscardModal = useCallback(() => setIsDiscardModalOpen(true), []);
  const closeDiscardModal = useCallback(() => setIsDiscardModalOpen(false), []);

  useEffect(() => {
    if (isOpenInternal && initialPrompt.length > 0) {
      setUserInput(initialPrompt);
    }
  }, [isOpenInternal, initialPrompt]);

  const regenerateContextId = useCallback((): string => {
    const newId = v4();

    setContextId(newId);

    return newId;
  }, [setContextId]);

  const createAbortController = useCallback((): AbortController => {
    const newController = new AbortController();
    abortControllerRef.current = newController;

    return newController;
  }, []);

  const [brief, setBrief] = useState<Brief | undefined>(
    initialBrief && normalizeLineEndings({ ...initialBrief, sections: enrichBriefSectionsWithEvents(initialBrief) })
  );

  const inJpHighSpendExperienceEffective = useMemo(
    () => isHighSpendExperience({ brief, inJpHighSpendExperience }),
    [inJpHighSpendExperience, brief]
  );

  const resetBriefCreationState = useCallback(
    (shouldGoBackToInitialStep = false) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      // Clear brief state for clean slate
      setBrief(undefined);

      // Clear classification state that contains initial content for some sections
      setBriefClassification(undefined);

      // Clear user input
      setUserInput('');

      // Clear React Query cache to ensure there's no persisted/cached data
      queryClient.clear();

      // Force re-generation of new context ID
      regenerateContextId();

      if (shouldGoBackToInitialStep) {
        setStep(initialStep);
      }
    },
    [setBrief, setBriefClassification, setUserInput, queryClient, regenerateContextId, setStep, initialStep]
  );

  useEffect(() => {
    const shouldReset = !isOpenInternal && hasBeenOpened.current;

    if (shouldReset) {
      resetBriefCreationState(true);
    }

    if (isOpenInternal) {
      hasBeenOpened.current = true;
    }
  }, [isOpenInternal, resetBriefCreationState]);

  const missingFields = useMemo(() => brief?.sections?.filter(isMandatoryAndEmpty), [brief?.sections]);

  const setStepWrapper = useCallback(
    (step: Step | SetStateAction<Step>) => {
      if (typeof step === 'function') {
        setStepInternal(step(currentStep));
      } else {
        setStepInternal(Number(step));
      }
      setStep(step);
    },
    [setStepInternal, currentStep, setStep]
  );

  const setIsOpenWrapper = useCallback(
    (isOpen: boolean) => {
      setIsOpenInternal(isOpen);

      if (setIsOpen) {
        setIsOpen(isOpen);
      }
    },
    [setIsOpenInternal, setIsOpen]
  );

  const setBriefWrapper = useCallback(
    (brief: any) => {
      setBrief((prevBrief) => {
        let newBrief = brief;

        if (typeof brief === 'function') {
          newBrief = brief(prevBrief);
        }

        if (newBrief?.sections) {
          newBrief.sections = enrichBriefSectionsWithEvents(newBrief);
        }

        return normalizeLineEndings(newBrief);
      });
    },
    [setBrief, enrichBriefSectionsWithEvents]
  );

  const focusFirstMissingSection = useCallback(() => {
    if (!missingFields?.length) {
      return;
    }

    const [firstMissingSection] = missingFields;

    setBriefWrapper((prevBrief: Brief) => {
      if (!prevBrief?.sections?.length) {
        return prevBrief;
      }

      const updatedSections = prevBrief.sections.map((section: BriefSection) => ({
        ...section,
        focused: section.id === firstMissingSection.id,
      }));

      const updatedBrief = {
        ...prevBrief,
        sections: updatedSections,
      };

      return updatedBrief;
    });
  }, [missingFields, setBriefWrapper]);

  return (
    <>
      <BriefCreationContext.Provider
        value={{
          rollouts,
          initialStep: actualInitialStep,
          currentStep,
          setStep: setStepWrapper,
          isOpen: isOpenInternal,
          setIsOpen: setIsOpenWrapper,
          brief,
          setBrief: setBriefWrapper,
          contextId,
          regenerateContextId,
          userInput,
          setUserInput,
          isBriefLoading,
          setIsBriefLoading,
          briefClassification,
          setBriefClassification,
          placeholder,
          intentClassification,
          setIntentClassification,
          currency,
          missingFieldsCount: missingFields?.length || 0,
          focusFirstMissingSection,
          source,
          parentElement,
          onSuccess,
          buyerType,
          setBuyerType,
          createAbortController,
          onOpen,
          variant,
          briefInitialValues,
          isAllocatedAuctionInviteFlow,
          setIsAllocatedAuctionInviteFlow,
          enableEnhancedUx,
          enableScreeningQuestions,
          isDiscardModalOpen,
          openDiscardModal,
          closeDiscardModal,
          inJpHighSpendExperience: inJpHighSpendExperienceEffective,
        }}
      >
        <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
      </BriefCreationContext.Provider>
    </>
  );
};

export const useBriefCreationContext = createUseContext<BriefCreationContextProps | null>(BriefCreationContext);
